import { HttpClient } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { DEFAULT_SYSTEM_CATEGORIES_ORDER } from 'shared/constants/default-system-categories-order.constants';
import { supportedLanguages } from 'shared/constants/languages';
import {
    CategoryGroup,
    CategoryReorderResponse,
    CategoryReorderTypeEnum,
    CategoryReorderTypeToIceCategoryOrderSourceMap,
    IceCategoryOrderSourceEnum,
    MappedCategoryReorder,
} from 'shared/interfaces/category-reorder.interface';
import { ToastService } from 'shared/services/toast.service';

const VISUALS_API_URL = environment.BASE_URL + 'visualManagementApi';

@Injectable({
    providedIn: 'root',
})
export class CategoryListReorderService {
    constructor(
        private httpClient: HttpClient,
        private translateService: TranslateService,
        private toastService: ToastService,
    ) {}

    categoryOrderType = signal<CategoryReorderTypeEnum | null>(null);
    categoryOrderType$ = toObservable(this.categoryOrderType);

    supplierId = signal<string | null>(null);
    supplierId$ = toObservable(this.supplierId);

    chainCode = signal<string | null>(null);
    chainCode$ = toObservable(this.chainCode);

    listingId = signal<string | null>(null);
    listingId$ = toObservable(this.listingId);

    preconditionFailed = signal<boolean>(false);

    refreshCategoriesTrigger = new BehaviorSubject<void>(undefined);

    showCategoryReorderTabOnSupplier = signal<boolean>(false);
    showCategoryReorderTabOnBrand = signal<boolean>(false);

    categoryResetOrderType = signal<CategoryReorderTypeEnum | null>(null);
    categoryResetOrderType$ = toObservable(this.categoryResetOrderType);

    brandDataForResetCall = signal<any>(null);

    iceCategoriesOrderSource = signal<IceCategoryOrderSourceEnum | null>(null);

    effectiveCategorySource = computed<CategoryReorderTypeEnum | null>(() => {
        const iceCategoriesOrderSource = this.iceCategoriesOrderSource();
        return CategoryReorderTypeToIceCategoryOrderSourceMap[iceCategoriesOrderSource!];
    });

    currentLang = toSignal(this.translateService.onLangChange.pipe(switchMap(() => [this.translateService.getCurrentLang()])), {
        initialValue: this.translateService.getCurrentLang(),
    });

    currentLangName = computed(() => {
        const langCode = this.currentLang();
        return supportedLanguages.find((lang) => lang.languageCode === langCode)?.label || 'English';
    });

    categoriesOrderOverride = signal<MappedCategoryReorder[] | null>(null);

    categoriesUpdated = signal<boolean>(false);
    userReorganizedAssets = signal<boolean>(false);

    shouldDisplayLeavingPageWarning = computed(() => {
        if (this.categoryOrderType() !== CategoryReorderTypeEnum.LISTING) {
            return false;
        }
        return this.categoriesUpdated() && !this.userReorganizedAssets();
    });

    mappedCategories = computed<MappedCategoryReorder[]>(() => {
        const override = this.categoriesOrderOverride();
        if (override) {
            return override;
        }

        return this.categoriesOrder()
            .map((group) => this.mapCategoryGroupToMappedCategoryReorder(group))
            .sort((categoryA, categoryB) => categoryA.order - categoryB.order);
    });

    mappedCategoriesAfterReset = computed<MappedCategoryReorder[]>(() => {
        return this.categoriesAferReset()
            .map((group) => this.mapCategoryGroupToMappedCategoryReorder(group))
            .sort((categoryA, categoryB) => categoryA.order - categoryB.order);
    });

    categoriesOrder = toSignal(
        combineLatest([this.categoryOrderType$, this.supplierId$, this.chainCode$, this.listingId$, this.refreshCategoriesTrigger]).pipe(
            switchMap(([levelType, supplierId, chainCode, listingId]) => {
                if (levelType === CategoryReorderTypeEnum.SUPPLIER && supplierId) {
                    return this.getSupplierCategoriesOrder(supplierId, true);
                }
                if (levelType === CategoryReorderTypeEnum.BRAND && chainCode && supplierId) {
                    return this.getBrandCategoriesOrder(chainCode, supplierId, true);
                }
                if (levelType === CategoryReorderTypeEnum.LISTING && listingId) {
                    return this.getListingCategoriesOrder(listingId, true);
                }
                return of([] as CategoryGroup[]);
            }),
        ),
        { initialValue: [] as CategoryGroup[] },
    );

    categoriesAferReset = toSignal(
        // We need to get the categories of the upper level so we can display them in the reset modal

        combineLatest([this.categoryResetOrderType$, this.supplierId$]).pipe(
            switchMap(([resetLevelType, supplierId]) => {
                if (resetLevelType === CategoryReorderTypeEnum.SYSTEM) {
                    return of(this.systemDefaultCategoriesOrder().groups);
                }
                if (resetLevelType === CategoryReorderTypeEnum.SUPPLIER && supplierId) {
                    return this.getSupplierCategoriesOrder(supplierId);
                }
                if (resetLevelType === CategoryReorderTypeEnum.BRAND && this.brandDataForResetCall()) {
                    const chainCode = this.brandDataForResetCall().code;
                    const supplierIdForReset = this.brandDataForResetCall().supplier.id;
                    return this.getBrandCategoriesOrder(chainCode, supplierIdForReset);
                }
                return of([]);
            }),
        ),
        { initialValue: [] },
    );

    mapCategoryGroupToMappedCategoryReorder(categoryGroup: CategoryGroup): MappedCategoryReorder {
        return {
            id: categoryGroup.groupId,
            order: categoryGroup.sortOrder,
            name: categoryGroup.groupTranslations.find((translation) => translation.language === this.currentLangName())?.name || '',
            subCategories:
                categoryGroup.iceCategories.length > 1
                    ? categoryGroup.iceCategories
                          .map((category) => {
                              return {
                                  id: category.categoryId,
                                  order: category.sortOrder,
                                  name:
                                      category.categoryTranslations.find((translation) => translation.language === this.currentLangName())
                                          ?.name || '',
                              };
                          })
                          .sort((categoryA, categoryB) => categoryA.order - categoryB.order)
                    : undefined,
        };
    }

    getSupplierCategoriesOrder(supplierId: string, saveCategorysOrderSource: boolean = false) {
        return this.httpClient
            .get<CategoryReorderResponse>(`${VISUALS_API_URL}/listing-assets/categories-order/supplier?supplierId=${supplierId}`)
            .pipe(
                tap((response) => {
                    if (saveCategorysOrderSource) {
                        this.iceCategoriesOrderSource.set(response.source);
                    }
                    this.categoriesOrderOverride.set(null);
                }),
                map((response) => response.groups),
                catchError(() => of([])),
            );
    }

    getBrandCategoriesOrder(chainCode: string, supplierId: string, saveCategorysOrderSource: boolean = false) {
        return this.httpClient
            .get<CategoryReorderResponse>(
                `${VISUALS_API_URL}/listing-assets/categories-order/brand?supplierId=${supplierId}&chainCode=${chainCode}`,
            )
            .pipe(
                tap((response) => {
                    if (saveCategorysOrderSource) {
                        this.iceCategoriesOrderSource.set(response.source);
                    }
                    this.categoriesOrderOverride.set(null);
                }),
                map((response) => response.groups),
                catchError(() => of([])),
            );
    }

    getListingCategoriesOrder(listingId: string, saveCategorysOrderSource: boolean = false) {
        return this.httpClient
            .get<CategoryReorderResponse>(`${VISUALS_API_URL}/listing-assets/categories-order/listing?listingId=${listingId}`)
            .pipe(
                tap((response) => {
                    if (saveCategorysOrderSource) {
                        this.iceCategoriesOrderSource.set(response.source);
                    }
                    this.categoriesOrderOverride.set(null);
                    this.preconditionFailed.set(false);
                }),
                map((response) => response.groups),
                catchError((error) => {
                    if (error.status === 412) {
                        this.preconditionFailed.set(true);
                    }
                    return of([]);
                }),
            );
    }

    resetSupplierCategoriesOrder(): void {
        const supplierId = this.supplierId();
        if (supplierId) {
            this.httpClient.get(`${VISUALS_API_URL}/listing-assets/categories-order/supplier/reset?supplierId=${supplierId}`).subscribe({
                next: () => {
                    this.toastService.showToast('visuals.photoOrganizer.toast.reset.supplier.success.label', 'success');
                    this.refreshCategoriesTrigger.next();
                },
                error: (error) => {
                    this.toastService.showToast('visuals.photoOrganizer.toast.reset.supplier.error.label', 'error');
                },
            });
        }
    }

    resetBrandCategoriesOrder(): void {
        const chainCode = this.chainCode();
        const supplierId = this.supplierId();
        if (chainCode) {
            this.httpClient
                .get(`${VISUALS_API_URL}/listing-assets/categories-order/brand/reset?chainCode=${chainCode}&supplierId=${supplierId}`)
                .subscribe({
                    next: () => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.reset.brand.success.label', 'success');
                        this.refreshCategoriesTrigger.next();
                    },
                    error: (error) => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.reset.brand.error.label', 'error');
                    },
                });
        }
    }

    resetListingCategoriesOrder(): void {
        const listingId = this.listingId();
        if (listingId) {
            this.httpClient.get(`${VISUALS_API_URL}/listing-assets/categories-order/listing/reset?listingId=${listingId}`).subscribe({
                next: () => {
                    this.toastService.showToast('visuals.photoOrganizer.toast.reset.listing.success.label', 'success');
                    this.refreshCategoriesTrigger.next();
                },
                error: (error) => {
                    this.toastService.showToast('visuals.photoOrganizer.toast.reset.listing.error.label', 'error');
                },
            });
        }
    }

    reorganizeAssets(keepCurrentListingSignature: boolean, keepCurrentOTACategorySignature: boolean): void {
        const listingId = this.listingId();
        if (listingId) {
            this.httpClient
                .get(
                    `${VISUALS_API_URL}/listing-assets/reorganize-assets?listingId=${listingId}&keepCurrentListingSignature=${keepCurrentListingSignature}&keepCurrentOTACategorySignature=${keepCurrentOTACategorySignature}`,
                )
                .subscribe({
                    next: () => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.reorganize.success.label', 'success');
                    },
                    error: (error) => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.reorganize.error.label', 'error');
                    },
                });
        }
    }

    updateCategories(categories: MappedCategoryReorder[]): void {
        const levelType = this.categoryOrderType();
        this.categoriesOrderOverride.set(categories);

        switch (levelType) {
            case CategoryReorderTypeEnum.SUPPLIER:
                this.updateSupplierCategories(categories);
                break;
            case CategoryReorderTypeEnum.BRAND:
                this.updateBrandCategories(categories);
                break;
            case CategoryReorderTypeEnum.LISTING:
                this.updateListingCategories(categories);
                break;
        }
    }

    updateSupplierCategories(categories: MappedCategoryReorder[]): void {
        const payload = this.buildCategoriesOrderPayload(categories);
        const supplierId = this.supplierId();

        if (supplierId) {
            this.httpClient
                .post(`${VISUALS_API_URL}/listing-assets/categories-order/supplier?supplierId=${supplierId}`, payload)
                .pipe(
                    catchError((error) => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.update.supplier.error.label', 'error');
                        this.categoriesOrderOverride.set(null);
                        return of(null);
                    }),
                )
                .subscribe((response) => {
                    if (response) {
                        this.toastService.showToast('visuals.photoOrganizer.toast.update.supplier.success.label', 'success');
                        this.refreshCategoriesTrigger.next();
                    }
                });
        }
    }

    updateBrandCategories(categories: MappedCategoryReorder[]): void {
        const payload = this.buildCategoriesOrderPayload(categories);
        const chainCode = this.chainCode();
        const supplierId = this.supplierId();

        if (chainCode) {
            this.httpClient
                .post(`${VISUALS_API_URL}/listing-assets/categories-order/brand?chainCode=${chainCode}&supplierId=${supplierId}`, payload)
                .pipe(
                    catchError((error) => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.update.brand.error.label', 'error');
                        this.categoriesOrderOverride.set(null);
                        return of(null);
                    }),
                )
                .subscribe((response) => {
                    if (response) {
                        this.toastService.showToast('visuals.photoOrganizer.toast.update.brand.success.label', 'success');
                        this.refreshCategoriesTrigger.next();
                    }
                });
        }
    }

    updateListingCategories(categories: MappedCategoryReorder[]): void {
        const payload = this.buildCategoriesOrderPayload(categories);
        const listingId = this.listingId();

        if (listingId) {
            this.httpClient
                .post(`${VISUALS_API_URL}/listing-assets/categories-order/listing?listingId=${listingId}`, payload)
                .pipe(
                    catchError((error) => {
                        this.toastService.showToast('visuals.photoOrganizer.toast.update.listing.error.label', 'error');
                        this.categoriesOrderOverride.set(null);
                        return of(null);
                    }),
                )
                .subscribe((response) => {
                    if (response) {
                        this.toastService.showToast('visuals.photoOrganizer.toast.update.listing.success.label', 'success');
                        this.refreshCategoriesTrigger.next();
                    }
                });
        }
    }

    private buildCategoriesOrderPayload(categories: MappedCategoryReorder[]) {
        return categories.map((category) => {
            const hasSubCategories = category.subCategories && category.subCategories.length > 0;

            if (hasSubCategories) {
                return {
                    groupId: category.id,
                    sortOrder: category.order,
                    iceCategories: category.subCategories?.map((subCategory) => ({
                        categoryId: subCategory.id,
                        sortOrder: subCategory.order,
                    })),
                };
            }

            const existingGroup = this.categoriesOrder().find((group) => group.groupId === category.id);
            const existingIceCategories = existingGroup?.iceCategories ?? [];

            return {
                groupId: category.id,
                sortOrder: category.order,
                iceCategories: existingIceCategories.map((iceCategory) => ({
                    categoryId: iceCategory.categoryId,
                    sortOrder: iceCategory.sortOrder,
                })),
            };
        });
    }

    systemDefaultCategoriesOrder = signal<CategoryReorderResponse>(DEFAULT_SYSTEM_CATEGORIES_ORDER);
}
